import React, { useState } from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Modal,
  Platform,
  TextInput,
  FlatList,
  useWindowDimensions,
} from "react-native";
import { useTranslation } from "../../../i18n/useTranslation";
import type { FeeInfo } from "../../../view-models/feeInfo";
import { FeeLookupHelper } from "../../../helper/feeLookupHelper";
import CloseIcon from "../../../../../../assets/images/CloseIcon.svg";
import BackIcon from "../../../../../../assets/images/BackIcon.svg";
import RadioSelectedIcon from "../../../../../../assets/images/RadioSelectButton.svg";
import Colors from "../../../constants/colors";
import LocationMap from "../../../../../../assets/images/Location.svg";
import SearchIcon from "../../../../../../assets/images/SearchIcon.svg";
import { ComponentStyles } from "../../../constants/componentStyles";
import { inputAssistDisabledProps } from "./FloatingTextInputComponent";

export interface AgentItem {
  AgentLogos?: any;
  ExchangeRate: number;
  Fee: number;
  Raw: any;
  ReceiveAgentAbbreviation: string;
  ReceiveAgentId: string;
  ReceiveAgentName: string;
}

export interface HowTheyReceiveModalProps {
  visible: boolean;
  title: string;
  subTitle: string;
  isCashPickUp?: boolean;
  selected?: string | null;
  onClose?: () => void;
  searchPlaceHolder?: string;
  searchValue?: string;
  filteredData?: AgentItem[];
  onSearchChangeText?: (text: string) => void;
  // renderItem: (item: T) => JSX.Element;
  howTheyReceiveModalTestID?: string
  onSelectWalletOption?: (item: AgentItem) => void;
}

// function HowTheyReceiveModal(props: HowTheyReceiveModalProps) {
const HowTheyReceiveModal: React.FC<HowTheyReceiveModalProps> = ({
  visible,
  title,
  subTitle,
  isCashPickUp,
  onClose,
  searchPlaceHolder,
  searchValue,
  onSearchChangeText,
  filteredData,
  howTheyReceiveModalTestID,
  onSelectWalletOption,
  selected
}) => {
  const { t } = useTranslation();
  const { height: windowHeight } = useWindowDimensions();
  /** Modal caps height; FlatList needs a bounded maxHeight or it expands and never scrolls. */
  const modalMaxHeight = Math.round(windowHeight * 0.88);
  /** Scroll viewport for delivery options (Popular partners / wallet list). */
  const optionsListMaxHeight = Math.round(windowHeight * 0.42);
  const colors = ["#FF6B6B", "#4ECDC4", "#FFD93D", "#6A4C93", "#1A535C"];
  const randomColor = (index: number) => colors[index % colors.length];

  const formatExchangeRate = (fee: FeeInfo): string => {
      const sendCurrency = fee?.SendAmounts?.SendCurrency || 'USD';
      const rate = FeeLookupHelper.getExchangeRate(fee);
      const receiveCurrency = FeeLookupHelper.getReceiveCurrency(fee);
      const rateNum = Number(rate);
      const rateFixed = isFinite(rateNum) ? rateNum.toFixed(4) : String(rate);
      return `1 ${sendCurrency} = ${rateFixed} ${receiveCurrency}`;
    };
  
    const formatFee = (fee: FeeInfo): string => {
      const amount = Number(fee?.SendAmounts?.TotalSendFees || 0);
      const currency = fee?.SendAmounts?.SendCurrency || 'USD';
      return `${amount.toFixed(2)} ${currency}`;
    };
  
  const getOptionKey = (fee: FeeInfo): string => {
      return JSON.stringify({
        sendCurrency: fee?.SendAmounts?.SendCurrency || null,
        exchangeRate: FeeLookupHelper.getExchangeRate(fee) || null,
        receiveCurrency: FeeLookupHelper.getReceiveCurrency(fee) || null,
        deliveryOptDisplayName: fee.DeliveryOptDisplayName || null,
        receiveAgentId: (fee as any).ReceiveAgentId || null,
      });
    };
  
  const getDeliveryTranslationKey = (item: any) => {
    return (
      item?.DeliveryOption ||
      item?.DeliveryOptId ||
      item?.DeliveryOptDisplayName
    );
  };

  const getFormattedDeliveryName = (item: any) => {
    const rawName =
      item?.ReceiveAgentName ||
      item?.Raw?.ReceiveAgentName ||
      item?.Raw?.DeliveryOptDisplayName ||
      '';

    const lower = rawName.toLowerCase();

    // 🟢 Mobile Wallet
    if (lower.includes('wallet')) {
      const agent = rawName.split('-').slice(1).join('-').trim();
      const base = t('delivery:MOBILE_WALLET_DEPOSIT');
      // return agent ? `${base} – ${agent}` : base;
      return agent ? `${agent}` : base;
    }

    // 🟡 Cash Pickup
    if (lower.includes('cash pickup')) {
      const location = rawName.split('-').slice(1).join('-').trim();
      const base = t('delivery:CASH_PICKUP');
      // return location ? `${base} – ${location}` : base;
      return location ? `${location}` : base;
    }

    // 🏦 Bank / account deposit — show partner name only (drop "Bank Deposit -" prefix)
    if (lower.includes('bank deposit')) {
      const withoutPrefix = rawName
        .replace(/^bank deposit\s*[-–—]\s*/i, '')
        .trim();
      return withoutPrefix || rawName;
    }

    // 🔵 Fallback (Home Delivery, etc.)
    return rawName;
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="none"
      onRequestClose={onClose}
    >
      <View style={styles.root}>
        <TouchableOpacity
          style={styles.scrim}
          activeOpacity={1}
          onPress={onClose}
        />
        <View style={[styles.modalContent, { maxHeight: modalMaxHeight }]}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 32}}>
            <TouchableOpacity onPress={onClose}>
              <BackIcon width={24} height={24}/>
            </TouchableOpacity>
            <TouchableOpacity onPress={onClose} testID={howTheyReceiveModalTestID}>
              <CloseIcon width={24} height={24}/>
            </TouchableOpacity>
          </View>
          {title && <Text style={styles.title}>{title}</Text>}
          {subTitle && <Text style={styles.subtitle}>{subTitle}</Text>}
          <View style={styles.searchContainer}>
            <SearchIcon width={24} height={24}/>
            <TextInput
              testID={howTheyReceiveModalTestID}
              style={styles.searchInput}
              placeholder={searchPlaceHolder}
              value={searchValue}
              onChangeText={onSearchChangeText}
              {...inputAssistDisabledProps}
            />
          </View>
          {
            isCashPickUp ? (
              <View style={{ marginTop: 32}}>
                {title && <Text style={styles.titleSecond}>Popular partners</Text>}
                {subTitle && <Text style={styles.subtitleSecond}>Pickup must be with the chosen partner</Text>}
              </View>
            ) : (
              <View style={{ marginBottom: 4}}>
              </View>
            )}
          <FlatList<AgentItem>
            testID={howTheyReceiveModalTestID}
            data={filteredData}
            keyExtractor={(item, index) => index.toString()}
            style={[styles.partnersFlatList, { maxHeight: optionsListMaxHeight }]}
            renderItem={({item, index})=>{
                      // const agentName = item?.ReceiveAgentName ? item?.ReceiveAgentName : item?.Raw?.DeliveryOptDisplayName;
                      const rawDisplayName =
                        item?.ReceiveAgentName ||
                        item?.Raw?.ReceiveAgentName ||
                        item?.Raw?.DeliveryOptDisplayName ||
                        '';
                      const agentName = t(`delivery:${rawDisplayName}`, {
                        defaultValue: rawDisplayName,
                      });
                      // const agentName = t(
                      //   `delivery:${getDeliveryTranslationKey(item?.Raw)}`,
                      //   {
                      //     defaultValue:
                      //       item?.ReceiveAgentName ||
                      //       item?.Raw?.DeliveryOptDisplayName ||
                      //       '',
                      //   }
                      // );
                      const itemKey = getOptionKey(item?.Raw);// fallback
                      
                      return (
                        <TouchableOpacity 
                          style={styles.methodRow}
                          onPress={() => {
                            onSelectWalletOption?.(item);
                          }}
                          testID={`countrySelected`}>
                          <View style={{ flex: 1, flexDirection: 'row', alignItems: 'center'}}>
                            <View
                              key={item?.ReceiveAgentId}
                              style={[
                                styles.providerCircle,
                                { backgroundColor: randomColor(index) },
                              ]}
                            />
                            <View style={{ paddingLeft: 12, flex: 1, marginRight: 10 }}>
                              <Text style={styles.countryName}>
                                {getFormattedDeliveryName(item)}
                                {/* {agentName} */}
                              </Text>
                              <View style={styles.rateRow}>
                                <Text style={styles.methodDetails}>{formatExchangeRate(item?.Raw)}</Text>
                                <View style={styles.dotStyle} />
                                <Text style={styles.methodDetails}>{formatFee(item?.Raw)}</Text>
                                <View style={styles.dotStyle} />
                                <Text style={styles.methodDetails}>
                                  {item.ReceiveAgentAbbreviation ?? "Instant"}
                                </Text>
                              </View>
                            </View>
                          </View>
                          <View style={styles.radioButton}>
                            {selected === itemKey && (
                              <RadioSelectedIcon width={20} height={20}/>
                            )}
                          </View>
                        </TouchableOpacity>
                      );
                    }}
            nestedScrollEnabled
            showsVerticalScrollIndicator
            keyboardShouldPersistTaps="handled"
          />
        </View>
      </View>
    </Modal>
  );
};

export default HowTheyReceiveModal;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingHorizontal: 24,
    paddingTop: 24,
    backgroundColor: Colors.white
  },
  modalOverlay: {
    flex: 1,
    justifyContent: "flex-end",
    backgroundColor: "rgba(0,0,0,0.4)"
  },
  root: {
    flex: 1,
    justifyContent: "flex-end",
    position: "fixed" as any, // web only
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  scrim: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: "rgba(0,0,0,0.4)",
  },

  modalContent: {
    backgroundColor: Colors.white,
    padding: 24,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    width: "100%",
    zIndex: 2, // ensures it stays above scrim
  },
  partnersFlatList: {
    width: "100%",
    flexGrow: 0,
  },
  closeBtn: {
    alignSelf: "flex-end",
    // padding: 24,
  },
  title: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.titleFont,
    fontWeight: '500', 
    color: Colors.titleTextColor,
    lineHeight: 34,
    marginBottom: 8
  },
  subtitle: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.subTitleFont,
    fontWeight: '400',
    lineHeight: 24,
    color: Colors.subTitleColor,
  },
  titleSecond: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: 16,
    fontWeight: '500', 
    color: Colors.titleTextColor,
    lineHeight: 20,
    marginBottom: 8
  },
  subtitleSecond: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 18,
    color: Colors.subTitleColor,
  },
  searchContainer: {
    flexDirection: 'row',
    backgroundColor: Colors.searchBackground,
    padding: 8,
    borderRadius: 24,
    // marginBottom: 19,
    marginTop: 16
  },
  searchInput: {
    flex: 1,
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.subTitleFont,
    fontWeight: "400",
    lineHeight: 24,
    color: Colors.subTitleColor,
    marginLeft: 8,
    // ...( { outlineWidth: 0 } as any ),
    ...(Platform.OS === 'web' && {
      outlineWidth: 0,
      outlineStyle: 'none',
      border: 'none',
      boxShadow: 'none',
    } as any),
  },
  methodRow: {
    flexDirection: "row",
    alignItems: 'center',
    paddingVertical: 20,
    // borderBottomWidth: 1,
    // borderBottomColor: "#E5E7EB",
  },
  flag: {
    fontSize: 22,
    marginRight: 12,
  },
  countryName: {
    fontSize: 16,
    fontWeight: "400",
    fontFamily: 'Rubik',
    lineHeight: 20,
    color: Colors.titleTextColor,
    flexShrink: 1,
  },
  providerCircle: { height: 32, width: 32, borderRadius: 50 },
  radioButton: {
    width: 20,
    height: 20,
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#D1D1D1'
  },
  rateRow: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: 4,
  },
  methodDetails: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: 12,
    fontWeight: '400',
    lineHeight: 16,
    color: Colors.titleTextColor,
    // marginBottom: 4
  },
  dotStyle: {
    height: 3,
    width: 3,
    borderRadius: 50,
    backgroundColor: Colors.titleTextColor,
    marginHorizontal: 4
  },
});